import React, { useState } from "react";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { BarChart3, Check, EyeOff } from "lucide-react";

/**
 * Interactive poll rendered inside a chat bubble.
 * meta.poll = { options: [{id, text}], multi, anonymous, votes: {optionId: [userId]} }
 * Voting posts the user's FULL selection; real-time updates arrive via the
 * conversation WebSocket (message_update merges meta back into the message).
 */
export function PollBubble({ message, conversation }) {
  const { user, t } = useApp();
  const poll = message.meta?.poll || {};
  const options = poll.options || [];
  const votes = poll.votes || {};
  const multi = !!poll.multi;
  const anonymous = !!poll.anonymous;
  const [busy, setBusy] = useState(false);
  const myVotes = options.filter((o) => (votes[o.id] || []).includes(user.id)).map((o) => o.id);
  const voterIds = new Set(Object.values(votes).flat());
  const totalVoters = voterIds.size;
  const maxCount = Math.max(1, ...options.map((o) => (votes[o.id] || []).length));
  const firstName = (uid) => {
    const p = (conversation?.participants || []).find((x) => x.id === uid);
    return p ? p.name.split(" ")[0] : "";
  };

  const toggleVote = async (oid) => {
    if (busy) return;
    let next;
    if (multi) next = myVotes.includes(oid) ? myVotes.filter((x) => x !== oid) : [...myVotes, oid];
    else next = myVotes.includes(oid) ? [] : [oid];
    setBusy(true);
    try {
      await api.post(`/messages/${message.id}/poll-vote`, { user_id: user.id, option_ids: next });
    } catch (e) { /* the WS update will resync the true state */ }
    setBusy(false);
  };

  return (
    <div className="min-w-[230px] sm:min-w-[280px]" data-testid="poll-bubble">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="font-bold text-xs uppercase tracking-wide text-primary">{t("poll")}</span>
        {anonymous && (
          <span className="flex items-center gap-0.5 text-[9px] text-muted-foreground uppercase tracking-wide" data-testid="poll-anonymous-badge">
            <EyeOff className="h-3 w-3" /> {t("poll_anonymous")}
          </span>
        )}
      </div>
      <p className="msg-text font-medium whitespace-pre-wrap break-words mb-2" data-testid="poll-question">{message.content}</p>
      <div className="space-y-1.5">
        {options.map((o) => {
          const count = (votes[o.id] || []).length;
          const pct = totalVoters ? Math.round((count / maxCount) * 100) : 0;
          const mine = myVotes.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`poll-option-${o.id}`}
              onClick={(e) => { e.stopPropagation(); toggleVote(o.id); }}
              disabled={busy}
              aria-pressed={mine}
              className={`relative w-full text-left rounded-lg border px-2.5 py-2 overflow-hidden transition-colors ${mine ? "border-primary/60" : "border-border hover:border-primary/40"}`}
            >
              {/* result bar */}
              <span
                className="absolute inset-y-0 left-0 bg-primary/15 transition-[width] duration-300"
                style={{ width: `${pct}%` }}
                aria-hidden
              />
              <span className="relative flex items-center gap-2">
                <span className={`h-4.5 w-4.5 h-[18px] w-[18px] shrink-0 flex items-center justify-center border transition-colors ${multi ? "rounded" : "rounded-full"} ${mine ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40 bg-background"}`}>
                  {mine && <Check className="h-3 w-3" />}
                </span>
                <span className="msg-text-sub flex-1 min-w-0 break-words">{o.text}</span>
                <span className="text-[11px] font-semibold text-muted-foreground tabular-nums shrink-0" data-testid={`poll-count-${o.id}`}>{count}</span>
              </span>
              {!anonymous && count > 0 && (
                <span className="relative block text-[10px] text-muted-foreground mt-0.5 ml-[26px] truncate" data-testid={`poll-voters-${o.id}`}>
                  {(votes[o.id] || []).map(firstName).filter(Boolean).join(", ")}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1.5" data-testid="poll-total">
        {totalVoters === 0 ? t("no_votes_yet") : `${totalVoters} ${totalVoters === 1 ? t("vote_one") : t("votes")}`}
        {multi ? ` \u00b7 ${t("poll_multi")}` : ""}
      </div>
    </div>
  );
}
